import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Grid,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WarningIcon from '@mui/icons-material/Warning';
import { useSiteStore } from '../../store/site.store';
import { useTransitionEligibility, useRequestTransition } from '../../hooks/useTransition';

const ModeTransitionPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id || 0;

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const { data: eligibility, isLoading, error } = useTransitionEligibility(siteId);
  const { mutate: requestTransition, isPending: isRequesting } = useRequestTransition(siteId);

  const handleRequestTransition = () => {
    requestTransition(undefined, {
      onSuccess: () => {
        setConfirmDialogOpen(false);
        // Show success message and redirect
        setTimeout(() => {
          navigate('/');
        }, 2000);
      },
    });
  };

  const getCriteriaIcon = (met: boolean) => {
    return met ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ELIGIBLE':
        return 'success';
      case 'GRACE_PERIOD':
        return 'warning';
      case 'NOT_ELIGIBLE':
        return 'default';
      case 'FORCE_TRANSITION':
        return 'info';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load transition eligibility. Please try again.
      </Alert>
    );
  }

  if (!eligibility) {
    return null;
  }

  const metCriteria = eligibility.criteria.filter(c => c.met).length;
  const totalCriteria = eligibility.criteria.length;
  const progress = (metCriteria / totalCriteria) * 100;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box mb={3} textAlign="center">
        <Typography variant="h4" component="h1" gutterBottom>
          Performance Mode Transition
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track your progress towards unlocking full analytics features
        </Typography>
      </Box>

      {/* Status Card */}
      <Card sx={{ mb: 3, bgcolor: eligibility.eligible ? 'success.light' : 'grey.100' }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            {eligibility.eligible ? (
              <RocketLaunchIcon sx={{ fontSize: 48, color: 'success.main' }} />
            ) : eligibility.gracePeriod ? (
              <HourglassEmptyIcon sx={{ fontSize: 48, color: 'warning.main' }} />
            ) : (
              <WarningIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            )}
            <Box flexGrow={1}>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Typography variant="h5">
                  {eligibility.eligible ? 'Ready to Transition!' : 
                   eligibility.gracePeriod ? 'Grace Period' : 'Not Yet Eligible'}
                </Typography>
                <Chip
                  label={eligibility.status.replace('_', ' ')}
                  color={getStatusColor(eligibility.status) as any}
                />
              </Box>
              <Typography variant="body1" color="text.secondary">
                {eligibility.message}
              </Typography>
            </Box>
          </Box>

          {eligibility.daysUntilEligible !== null && eligibility.daysUntilEligible > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {eligibility.gracePeriod 
                  ? `Auto-transition in ${eligibility.daysUntilEligible} days`
                  : `Estimated ${eligibility.daysUntilEligible} days until eligible`}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Progress Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Progress Overview
          </Typography>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">
                {metCriteria} of {totalCriteria} criteria met
              </Typography>
              <Typography variant="body2" color="primary" fontWeight="bold">
                {progress.toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5 }} />
          </Box>
        </CardContent>
      </Card>

      {/* Criteria Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Transition Criteria
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            {eligibility.criteria.map((criterion, index) => (
              <React.Fragment key={index}>
                <ListItem alignItems="flex-start">
                  <ListItemIcon sx={{ mt: 1 }}>
                    {getCriteriaIcon(criterion.met)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {criterion.name}
                        </Typography>
                        {criterion.met && <Chip label="Met" size="small" color="success" />}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
                          {criterion.description}
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              Current Value
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {criterion.currentValue}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              Required Value
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {criterion.requiredValue}
                            </Typography>
                          </Grid>
                        </Grid>
                      </>
                    }
                  />
                </ListItem>
                {index < eligibility.criteria.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* What's Next */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            What Happens After Transition?
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Unlock SEO Opportunities"
                secondary="Access detailed opportunity scoring and prioritization"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Unlock CRO Experiments"
                secondary="Run A/B tests and measure conversion impact"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Advanced Analytics"
                secondary="Access full performance metrics and trend analysis"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Keep Early-Stage Features"
                secondary="Readiness dashboard remains available for new pages"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box display="flex" gap={2} justifyContent="center">
        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate('/early-stage')}
        >
          Back to Dashboard
        </Button>
        {!eligibility.eligible && (
          <Button
            variant="contained"
            size="large"
            onClick={() => setConfirmDialogOpen(true)}
          >
            Request Early Transition
          </Button>
        )}
        {eligibility.eligible && (
          <Button
            variant="contained"
            size="large"
            color="success"
            startIcon={<RocketLaunchIcon />}
            onClick={() => setConfirmDialogOpen(true)}
            disabled={isRequesting}
          >
            {isRequesting ? 'Transitioning...' : 'Transition Now'}
          </Button>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>
          {eligibility.eligible ? 'Confirm Transition' : 'Request Early Transition'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {eligibility.eligible 
              ? 'Are you sure you want to transition to Performance Mode now? This will unlock all advanced features.'
              : 'You can request an early transition, but some features may show limited data initially. Are you sure you want to proceed?'}
          </Typography>
          {!eligibility.eligible && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Your site hasn't met all automatic transition criteria yet. Some analytics features may show "No data" messages initially.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleRequestTransition} 
            variant="contained" 
            color="primary"
            disabled={isRequesting}
          >
            {isRequesting ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModeTransitionPage;
